import { useTheme } from '@mui/material';
import { useConfig } from '@packages/shared/src/hooks/useConfig/useConfig';
import type { MutableRefObject, RefObject } from 'react';
import React, { useRef } from 'react';

/**
 * code is forked from https://github.com/denyo/react-circle-slider-hooked
 *
 * all unnecessary was removed
 */

function polarToCartesian(
  centerX: number,
  centerY: number,
  radius: number,
  angleInDegrees: number,
) {
  const angleInRadians = ((angleInDegrees - 90) * Math.PI) / 180.0;

  return {
    x: centerX + radius * Math.cos(angleInRadians),
    y: centerY + radius * Math.sin(angleInRadians),
  };
}

function buildPath({
  cx,
  cy,
  radius,
  startAngle,
  endAngle,
  thickness,
}: {
  cx: number;
  cy: number;
  radius: number;
  startAngle: number;
  endAngle: number;
  thickness: number;
}) {
  const start = polarToCartesian(cx, cy, radius, endAngle);
  const end = polarToCartesian(cx, cy, radius, startAngle);
  const largeArcFlag = endAngle - startAngle <= 180 ? '0' : '1';

  const cutoutRadius = radius - thickness;
  const start2 = polarToCartesian(cx, cy, cutoutRadius, endAngle);
  const end2 = polarToCartesian(cx, cy, cutoutRadius, startAngle);

  return [
    'M',
    start.x,
    start.y,
    'A',
    radius,
    radius,
    0,
    largeArcFlag,
    0,
    end.x,
    end.y,
    'L',
    cx,
    cy,
    'Z',
    'M',
    start2.x,
    start2.y,
    'A',
    cutoutRadius,
    cutoutRadius,
    0,
    largeArcFlag,
    0,
    end2.x,
    end2.y,
    'L',
    cx,
    cy,
    'Z',
  ].join(' ');
}

type Props = {
  value: number;
  min: number;
  max: number;
  onChange: (value: number) => void;
  size?: number;
};

const THRESHOLD = 0.1;
const circleWidth = 4;
const progressWidth = 4;
const knobRadius = 8;
const stepSize = 1;

export const Slider = ({ value, min, max, size = 180, onChange }: Props) => {
  const { palette } = useTheme();
  const {
    installmentCalculator: { hasSpecialCreditColors },
  } = useConfig();

  const progressColor = hasSpecialCreditColors ? palette.additional!.main : palette.primary.main;

  const formatValue = (input?: number) => Math.round((input || value) / stepSize) * stepSize;

  const valueToAngle = () => {
    let newAngle = 360;

    if (min < max) {
      newAngle = Math.round(((formatValue() - min) / (max - min)) * 360);
    }
    return newAngle === 360 ? newAngle - THRESHOLD : newAngle % 360;
  };

  const angleToValue = (newAngle: number) => formatValue(newAngle / (360 / (max - min))) + min;

  const svgRef = useRef<SVGSVGElement>(undefined) as RefObject<SVGSVGElement | null>;
  const prevX = useRef<number>(undefined) as MutableRefObject<number>; // necessary since functional component

  const updateSliderFromEvent = (event: MouseEvent | Touch) => {
    if (min === max) return;

    const rectSize = svgRef.current!.getBoundingClientRect();
    const rectCenter = rectSize.width / 2;

    const x = event.clientX - rectSize.left - rectCenter;
    const y = event.clientY - rectSize.top - rectCenter;
    const angleBetweenTwoVectors = Math.atan2(y, x);

    let newAngle = Math.round((angleBetweenTwoVectors * 180) / Math.PI) + 90;

    if (x < 0 && y < 0) {
      newAngle += 360;
    }

    // prevent jumping from "< 360 to > 0" and "> 0 to < 360"
    if (y < 0) {
      if (prevX.current < 0 && x > 0) {
        newAngle = 360 - THRESHOLD; // can't go over 360
      } else if (prevX.current > 0 && x < 0) {
        newAngle = THRESHOLD; // can't go lower than 0
      } else {
        prevX.current = x;
      }
    } else {
      prevX.current = x;
    }

    onChange(angleToValue(newAngle));
  };

  // mouse event handlers
  // --------------------

  // regular dom event
  const handleMouseMove = (event: MouseEvent) => {
    event.preventDefault();
    updateSliderFromEvent(event);
  };
  // regular dom event
  const handleMouseUp = (event: MouseEvent) => {
    event.preventDefault();
    svgRef.current!.removeEventListener('mousemove', handleMouseMove);
    svgRef.current!.removeEventListener('mouseup', handleMouseUp);
  };
  // react event
  const handleMouseDown = (event: React.MouseEvent) => {
    event.preventDefault();
    svgRef.current!.addEventListener('mousemove', handleMouseMove);
    svgRef.current!.addEventListener('mouseup', handleMouseUp);
  };

  // touch event handlers
  // --------------------
  // regular dom event
  const handleTouchMove = (event: TouchEvent) => {
    const { targetTouches } = event;
    const currentTouch = targetTouches[targetTouches.length - 1];
    if (currentTouch) {
      updateSliderFromEvent(currentTouch);
    }
  };
  // regular dom event
  const handleTouchUp = () => {
    svgRef.current!.removeEventListener('touchmove', handleTouchMove);
    svgRef.current!.removeEventListener('touchend', handleTouchUp);
  };
  // react event
  const handleTouchStart = () => {
    svgRef.current!.addEventListener('touchmove', handleTouchMove);
    svgRef.current!.addEventListener('touchend', handleTouchUp);
  };

  const center = size / 2;
  // if there is a shadow we need to draw a bit smaller to not cut if off
  const radius = center - Math.max(circleWidth, progressWidth, knobRadius * 2) / 2 - 10;
  const progressPath = buildPath({
    cx: center,
    cy: center,
    radius: radius + progressWidth / 2,
    startAngle: 0,
    endAngle: valueToAngle(),
    thickness: progressWidth,
  });
  const knobCenter = polarToCartesian(center, center, radius, valueToAngle());

  return (
    <svg
      ref={svgRef}
      height={size}
      width={size}
      viewBox={`0 0 ${size} ${size}`}
      onMouseDown={handleMouseDown}
      onTouchStart={handleTouchStart}
      style={{
        boxSizing: 'border-box',
        touchAction: 'none',
      }}
    >
      <circle
        style={{
          fill: 'none',
          stroke: progressColor,
          strokeWidth: circleWidth,
          opacity: 0.38,
        }}
        r={radius}
        cx={center}
        cy={center}
      />

      <path
        style={{
          fill: progressColor,
          fillRule: 'evenodd',
          stroke: 'none',
        }}
        d={progressPath}
      />

      <filter id="dropShadow" filterUnits="userSpaceOnUse">
        <feGaussianBlur in="SourceAlpha" stdDeviation="3" />
        <feOffset dx="2" dy="2" />
        <feComponentTransfer>
          <feFuncA type="linear" slope="0.3" />
        </feComponentTransfer>
        <feMerge>
          <feMergeNode />
          <feMergeNode in="SourceGraphic" />
        </feMerge>
      </filter>
      <circle
        style={{
          cursor: min === max ? 'default' : 'pointer',
          fill: progressColor,
        }}
        filter="url(#dropShadow)"
        r={knobRadius}
        cx={knobCenter.x}
        cy={knobCenter.y}
      />
    </svg>
  );
};
